import { Fragment, useMemo } from 'react';

type Condition<Value = unknown> = Value | Falsy;

/**
 * A component that renders children if the condition is truthy.
 * @param condition The condition to check.
 * @param children The children to render if the condition is truthy.
 * @param fallback The children to render if the condition is falsy.
 * @returns The children if the condition is truthy, otherwise the fallback.
 * @example
 * ```tsx
 * <If condition={true}>
 *  <p>It's true!</p>
 * </If>
 * ```
 * @example
 * ```tsx
 * <If condition={false} fallback={<p>It's false!</p>}>
 * <p>It's true!</p>
 * </If>
 * ```
 */

function If<Value = unknown>({
  condition,
  children,
  fallback,
}: React.PropsWithoutRef<{
  condition: Condition<Value>;
  children: React.ReactNode | ((value: Value) => React.ReactNode);
  fallback?: React.ReactNode;
}>) {
  return useMemo(() => {
    if (condition) {
      if (typeof children === 'function') {
        return <Fragment>{children(condition)}</Fragment>;
      }

      return <Fragment>{children}</Fragment>;
    }

    if (fallback) {
      return <Fragment>{fallback}</Fragment>;
    }

    return null;
  }, [condition, fallback, children]);
}

export default If;
